import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import LinklyTerminals from '../../components/LinklyTerminals';
import CashDrawerCard from '../../components/CashDrawerCard';
import { useLinklyEnabled } from '../../lib/linkly';
import {
  Activity, Database, CreditCard, Mail, Bot, Bell, Eye,
  RefreshCw, AlertTriangle, Shield, ShieldCheck, Key, Terminal, KeyRound, ServerCog, ImagePlus,
} from 'lucide-react';

function StatusDot({ ok, label, extra }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className={`w-2.5 h-2.5 rounded-full ${ok ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]' : 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.6)]'}`} />
      <span className="text-zinc-300 text-sm font-sans">{label}</span>
      {extra && <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans ml-auto">{extra}</span>}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-sm p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className="text-xl font-serif text-white mt-1" data-testid={`tech-count-${label.toLowerCase()}`}>{value}</p>
    </div>
  );
}

export default function TechPage() {
  const linklyEnabled = useLinklyEnabled();
  const [health, setHealth] = useState(null);
  const [flags, setFlags] = useState(null);
  const [logs, setLogs] = useState(null);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logBusy, setLogBusy] = useState(false);
  const [busyFlag, setBusyFlag] = useState(null);
  const [backdropJob, setBackdropJob] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [h, ff, fl] = await Promise.all([
        api.get('/tech/health'),
        api.get('/tech/failed-logins?limit=20'),
        api.get('/tech/feature-flags'),
      ]);
      setHealth(h.data);
      setFailed(ff.data);
      setFlags(fl.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tech data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000); // 30s auto-refresh
    return () => clearInterval(id);
  }, []);

  const toggleFlag = async (key, current) => {
    setBusyFlag(key);
    try {
      const { data } = await api.put(`/tech/feature-flags/${key}`, { value: !current });
      setFlags(prev => ({ ...prev, [key]: data.value }));
      toast.success(`${key.replace(/_/g, ' ')} → ${data.value ? 'ON' : 'OFF'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusyFlag(null);
    }
  };

  const loadLogs = async () => {
    setLogBusy(true);
    try {
      const { data } = await api.get('/tech/logs?lines=300');
      setLogs(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setLogBusy(false);
    }
  };

  const resetStaffPasswords = async () => {
    if (!window.confirm('Reset ALL staff passwords to the default? This affects every admin / manager / chef / bartender / waiter / tech account.')) return;
    try {
      const { data } = await api.post('/tech/reset-staff-passwords');
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  const productionReset = async () => {
    const ack = window.prompt(
      'DANGER · This permanently DELETES every order, payment transaction, service request, coal check, and stale notification, and resets every table to "available". Customer accounts, menu, staff, suppliers and inventory are kept.\n\nType RESET_MAJNU to confirm.'
    );
    if (ack !== 'RESET_MAJNU') {
      if (ack !== null) toast.error('Confirmation text did not match — aborted.');
      return;
    }
    try {
      const { data } = await api.post('/tech/production-reset', { confirm: 'RESET_MAJNU' });
      toast.success(data.message + ' · ' + Object.entries(data.deleted).map(([k, v]) => `${k}:${v}`).join(' · '));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  // Polls /api/tech/regenerate-backdrops/status while a regen is running so
  // the button shows live progress (4 categories, ~75s total). Stops once
  // the backend reports status='done'.
  const pollBackdropJob = async () => {
    try {
      const { data } = await api.get('/tech/regenerate-backdrops/status');
      setBackdropJob(data);
      if (data.status === 'done') {
        const errs = (data.errors || []).length;
        if (errs === 0) toast.success(`Backdrops regenerated — ${data.done.length}/4 ready`);
        else toast.error(`Backdrops finished with ${errs} error(s) — check logs`);
        return;
      }
      if (data.status === 'running') {
        setTimeout(pollBackdropJob, 3000);
      }
    } catch (e) {
      void e;
    }
  };
  const regenerateBackdrops = async () => {
    if (backdropJob?.status === 'running') {
      toast.info('Regen already in progress…');
      return;
    }
    const ack = window.confirm('Regenerate all 4 category backdrops via Gemini Nano Banana?\n\nThis takes ~75 seconds total. New images replace the current Drinks / Food / Shisha / Play backdrops on the customer menu.');
    if (!ack) return;
    try {
      const { data } = await api.post('/tech/regenerate-backdrops', {});
      if (data.status === 'already_running') {
        toast.info('A regen is already running.');
      } else {
        toast.success('Backdrop regen started — polling for progress…');
      }
      setBackdropJob({ status: 'running', current: null, done: [], errors: [] });
      setTimeout(pollBackdropJob, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start regen');
    }
  };

  const auditStaffAccounts = async () => {
    try {
      const { data } = await api.get('/tech/staff-accounts');
      const bad = (data || []).filter(r => r.status !== 'OK');
      if (bad.length === 0) {
        toast.success('All staff accounts OK — canonical email + role + active.');
        return;
      }
      const lines = bad.map(r => `• ${r.email}: ${r.status}`).join('\n');
      window.alert(`Issues detected on ${bad.length} staff account(s):\n\n${lines}\n\nUse "Fix a Staff Account" to repair.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
    }
  };

  const fixStaffAccount = async () => {
    const email = window.prompt(
      'Repair a single staff account by email.\nThis: (1) deletes any duplicate rows, (2) restores the canonical role, (3) resets the password to the default, (4) clears brute-force lockouts.\n\nEnter the email (e.g. manager@majnu.com.au):'
    );
    if (!email) return;
    try {
      const { data } = await api.post('/tech/fix-staff-account', { email: email.trim() });
      toast.success(`${data.email} repaired → role: ${data.role}, password: ${data.password}${data.duplicates_removed ? ` (removed ${data.duplicates_removed} duplicate row${data.duplicates_removed > 1 ? 's' : ''})` : ''}`, { duration: 10000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fix failed');
    }
  };

  const resetModules = async () => {
    if (!window.confirm('Reset all module feature flags so every section is visible again. Use this if sections like Insights, Staff Management or Tech disappeared from the sidebar. Continue?')) return;
    try {
      const { data } = await api.post('/tech/reset-modules');
      toast.success(data.message + ' Reload the page to see the sidebar refresh.', { duration: 10000 });
      // Force a refresh so the module flags are picked up immediately.
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  if (loading && !health) return <p className="text-zinc-500 font-sans">Loading health…</p>;

  return (
    <div className="space-y-6" data-testid="tech-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif text-white flex items-center gap-2">
            <ServerCog className="w-5 h-5 text-gold" /> Tech Panel
          </h1>
          <p className="text-sm text-zinc-500 font-sans">System health, feature flags, logs.</p>
        </div>
        <Button
          onClick={fetchAll}
          variant="outline"
          className="border-white/10 text-zinc-300 hover:text-white rounded-sm font-sans text-xs"
          data-testid="tech-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* === Service status === */}
      <section className="bg-white/[0.03] border border-white/10 rounded-sm p-4" data-testid="tech-services">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-3 flex items-center gap-2">
          <Activity className="w-3 h-3" /> Services
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <StatusDot ok={health?.services?.mongodb?.ok} label="MongoDB" extra={health?.services?.mongodb?.ok ? 'CONNECTED' : (health?.services?.mongodb?.error || 'ERROR')} />
          <StatusDot ok={health?.services?.stripe?.ok} label="Stripe" extra={health?.services?.stripe?.mode?.toUpperCase()} />
          <StatusDot ok={health?.services?.resend?.ok} label="Resend (Email)" extra={health?.services?.resend?.ok ? 'KEY SET' : 'MISSING'} />
          <StatusDot ok={health?.services?.emergent_llm?.ok} label="Emergent LLM" extra={health?.services?.emergent_llm?.ok ? 'KEY SET' : 'MISSING'} />
          <StatusDot ok={health?.services?.push_vapid?.ok} label="Push / VAPID" extra={health?.services?.push_vapid?.ok ? 'KEY SET' : 'MISSING'} />
        </div>
        {health?.server_time_utc && (
          <p className="text-[10px] text-zinc-600 font-sans mt-3">Server time (UTC): {health.server_time_utc}</p>
        )}
      </section>

      {/* === Counts === */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-2 flex items-center gap-2">
          <Database className="w-3 h-3" /> Data Counts
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatCard icon={Database} label="users" value={health?.counts?.users ?? '—'} />
          <StatCard icon={Database} label="menu_items" value={health?.counts?.menu_items ?? '—'} />
          <StatCard icon={Database} label="orders" value={health?.counts?.orders ?? '—'} />
          <StatCard icon={Database} label="tables" value={health?.counts?.tables ?? '—'} />
          <StatCard icon={Database} label="inventory" value={health?.counts?.inventory_items ?? '—'} />
        </div>
      </section>

      {/* === Feature flags === */}
      <section className="bg-white/[0.03] border border-white/10 rounded-sm p-4" data-testid="tech-flags">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-3 flex items-center gap-2">
          <Shield className="w-3 h-3" /> Feature Flags
        </p>
        <div className="space-y-2">
          {flags && Object.entries(flags).map(([key, value]) => {
            const isKill = key === 'maintenance_mode';
            return (
              <div key={key} className={`flex items-center justify-between py-2 px-3 rounded-sm ${isKill && value ? 'bg-red-500/10 border border-red-500/30' : 'bg-black/20'}`}>
                <div>
                  <p className="text-white text-sm font-sans">{key.replace(/_/g, ' ')}</p>
                  {isKill && value && <p className="text-red-400 text-[11px] font-sans">⚠️ App is in maintenance mode — customers can't place orders</p>}
                </div>
                <Switch
                  checked={!!value}
                  disabled={busyFlag === key}
                  onCheckedChange={() => toggleFlag(key, value)}
                  data-testid={`flag-${key}`}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* === Ops === */}
      <section className="bg-white/[0.03] border border-white/10 rounded-sm p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-3 flex items-center gap-2">
          <KeyRound className="w-3 h-3" /> Operations
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Button
            onClick={resetStaffPasswords}
            variant="outline"
            className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 rounded-sm font-sans text-xs justify-start"
            data-testid="tech-reset-passwords"
          >
            <KeyRound className="w-3.5 h-3.5 mr-2" /> Reset all staff passwords
          </Button>
          <Button
            onClick={auditStaffAccounts}
            variant="outline"
            className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 rounded-sm font-sans text-xs justify-start"
            data-testid="tech-audit-staff"
          >
            <ShieldCheck className="w-3.5 h-3.5 mr-2" /> Audit staff accounts
          </Button>
          <Button
            onClick={fixStaffAccount}
            variant="outline"
            className="border-amber-500/40 text-amber-200 hover:bg-amber-500/15 rounded-sm font-sans text-xs justify-start"
            data-testid="tech-fix-staff"
          >
            <Key className="w-3.5 h-3.5 mr-2" /> Fix a staff account (by email)
          </Button>
          <Button
            onClick={resetModules}
            variant="outline"
            className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 rounded-sm font-sans text-xs justify-start"
            data-testid="tech-reset-modules"
          >
            <Eye className="w-3.5 h-3.5 mr-2" /> Reset modules (show all sections)
          </Button>
          <Button
            onClick={loadLogs}
            disabled={logBusy}
            variant="outline"
            className="border-white/10 text-zinc-300 hover:text-white rounded-sm font-sans text-xs justify-start"
            data-testid="tech-load-logs"
          >
            <Terminal className="w-3.5 h-3.5 mr-2" /> Load backend logs (last 300 lines)
          </Button>
          <Button
            onClick={regenerateBackdrops}
            disabled={backdropJob?.status === 'running'}
            variant="outline"
            className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 rounded-sm font-sans text-xs justify-start"
            data-testid="tech-regenerate-backdrops"
          >
            <ImagePlus className="w-3.5 h-3.5 mr-2" />
            {backdropJob?.status === 'running'
              ? `Regenerating… ${backdropJob.current ? `${backdropJob.current} ` : ''}(${(backdropJob.done || []).length}/4)`
              : 'Regenerate Category Backdrops (Drinks · Food · Shisha · Play)'}
          </Button>
          <Button
            onClick={productionReset}
            variant="outline"
            className="border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm font-sans text-xs justify-start col-span-1 md:col-span-2"
            data-testid="tech-production-reset"
          >
            <AlertTriangle className="w-3.5 h-3.5 mr-2" /> Production Reset — wipe orders / payments / service reqs / coal checks
          </Button>
        </div>
      </section>

      {/* === Failed logins === */}
      <section className="bg-white/[0.03] border border-white/10 rounded-sm p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-3 h-3" /> Recent Failed Logins ({failed.length})
        </p>
        {failed.length === 0 ? (
          <p className="text-zinc-500 text-sm font-sans">No failed logins recorded.</p>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {failed.map((f, i) => (
              <div key={`failed-${f.timestamp || ''}-${i}`} className="flex items-center justify-between text-xs font-sans">
                <span className="text-zinc-300 truncate max-w-[60%]">{f.identifier || '—'}</span>
                <span className="text-zinc-500">{f.timestamp?.slice(0, 19)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* === Logs === */}
      {logs && (
        <section className="bg-black border border-white/10 rounded-sm p-4" data-testid="tech-logs">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-3 flex items-center gap-2">
            <Terminal className="w-3 h-3" /> Logs
          </p>
          {logs.logs?.map((lg, i) => (
            <div key={`log-${lg.file || i}`} className="mb-4">
              <p className="text-[10px] text-zinc-600 font-mono mb-1">{lg.file}</p>
              <pre className="text-[11px] text-emerald-300 font-mono bg-black/60 p-2 rounded-sm overflow-x-auto max-h-64">
                {lg.lines.join('')}
              </pre>
            </div>
          ))}
        </section>
      )}

      {/* Linkly Cloud EFTPOS — hidden until LINKLY_ENABLED + URLs are set
          in the backend env. Once configured, the merchant pairs each
          Move5000 terminal here. Stays out of the way until then. */}
      {linklyEnabled && <LinklyTerminals />}
      <CashDrawerCard />
    </div>
  );
}
